// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { newsListViewStyles } from "@conklin/Overrides/Widgets/NewsList/NewsListView";
import IconLinkFilter from "@conklin/XDL/Icons/IconLinkFilter";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, linkArrowStyle, xScreen } from "@conklin/XDL/Utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

newsListViewStyles.listContainer = {
    css: css`
        gap: 22px 24px;
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 16px;
        ${xScreen("lte", "md")} {
            margin-bottom: 24px;
            margin-top: 16px;
            gap: 24px 20px;
        }
    `,
};

newsListViewStyles.content = {
    css: css`
        width: calc((100% - 48px) / 3);
        ${xScreen("lte", "md")} {
            width: calc((100% - 24px) / 2);
        }

        ${xScreen("lt", "sm")} {
            width: 100%;
        }
    `,
};
newsListViewStyles.pagination = {
    cssOverrides: {
        pagination: css`
            &&& {
                padding: 0;
                border-top: 0;
                margin: 0;
            }
        `,
    },
};
newsListViewStyles.paginationWrapper = {
    css: css`
        background-color: ${color("common.ui2")};
        border-radius: 4px;
        padding: 8px;
        min-height: 42px;
        display: flex;
        justify-content: flex-end;
        margin: 16px 0;
    `,
};
newsListViewStyles.paginationWithFiltersWrapper = {
    css: css`
        ${newsListViewStyles.paginationWrapper.css}
        ${xScreen("lte", "md")} {
            justify-content: space-between;
        }
    `,
};
newsListViewStyles.mobileFiltersLink = {
    ...createLinkIconStyles(IconLinkFilter, "default", "left", false, {
        typographyProps: {
            variant: "h5",
            css: css`
                margin-bottom: 0;
            `,
        },
        css: css`
            &&& {
                padding: 4px 0;
                display: none;
                ${xScreen("lte", "md")} {
                    display: inline-flex;
                }
            }
        `,
    }),
};
newsListViewStyles.doneBtn = {
    css: css`
        width: 100%;
        margin-top: 16px;
    `,
};
newsListViewStyles.drawerTitle = {
    color: "text.main",
    variant: "h5",
    css: css`
        margin-bottom: 0;
    `,
};
newsListViewStyles.drawerTitleWrapper = {
    css: css`
        margin-bottom: 16px;
    `,
};
newsListViewStyles.drawer = {
    cssOverrides: {
        drawerBody: css`
            ${xScreen("lt", "sm")} {
                width: 100%;
            }
        `,
    },
    mergeCss: true,
};

newsListViewStyles.wrapper = {
    css: css`
        display: flex;
        width: 100%;
        height: 100%;
        flex-direction: column;
        @media print {
            page-break-inside: avoid;
        }
    `,
};

newsListViewStyles.imageClickableWrapper = {
    css: css`
        flex: 0 0 auto;
        max-height: 100%;
        overflow: hidden;
        border-radius: 4px;

        height: 161px;
        width: 100%;
        text-align: center;
        ${xScreen("lte", "lg")} {
            height: 136px;
        }
        ${xScreen("lte", "md")} {
            height: 154px;
        }
        ${xScreen("lte", "sm")} {
            height: 141px;
        }
        & > div {
            height: auto;
            width: auto;
            max-height: 100%;
            max-width: 100%;
            aspect-ratio: 368/161;
            ${xScreen("lte", "lg")} {
                aspect-ratio: 317 / 136;
            }
            ${xScreen("lte", "md")} {
                aspect-ratio: 358 / 154;
            }
            ${xScreen("lte", "sm")} {
                aspect-ratio: 328 / 141;
            }
        }
    `,
};

newsListViewStyles.image = {
    css: css`
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        aspect-ratio: 368/161;

        ${xScreen("lte", "lg")} {
            aspect-ratio: 317/136;
        }
        ${xScreen("lte", "md")} {
            aspect-ratio: 358/154;
        }
        ${xScreen("lte", "sm")} {
            aspect-ratio: 328/141;
        }
    `,
};

newsListViewStyles.cardContentWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        flex: 1;
        padding-top: 16px;
    `,
};

newsListViewStyles.subtitle = {
    typographyProps: {
        size: 12,
        weight: fontConfig.weight.bold,
        css: css`
            min-height: 15px;
            margin-bottom: 4px;
            line-height: 15px;
            ${ellipsis(1)}
        `,
    },
    css: css`
        &:hover {
            & > ${TypographyStyle} {
                color: ${color("common.ui10")};
            }
        }
    `,
};

newsListViewStyles.title = {
    typographyProps: {
        forwardAs: "h2",
        variant: "h5",
        css: css`
            margin-bottom: 8px;
            min-height: 38px;
            ${ellipsis(2)}
        `,
    },
};

newsListViewStyles.textContent = {
    variant: "p",
    size: 14,
    css: css`
        margin-bottom: 16px;
        line-height: 22px;
        ${ellipsis(4)}
        ${TypographyStyle} {
            font-size: 14px;
            line-height: 22px;
            margin-bottom: 0;
        }
    `,
};

newsListViewStyles.smallLink = linkArrowStyle();
newsListViewStyles.link = linkArrowStyle("large");
newsListViewStyles.buttonWrapper = {
    css: css`
        margin-top: auto;
    `,
};
